import React from 'react';
import { CardContent } from "@/components/ui/card";
import { CheckCircle, LucideIcon } from 'lucide-react';
import { SimpleReveal } from '@/hooks/useSimpleAnimation';

interface PatentCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  callouts: string[];
  alignment?: 'left' | 'center';
  delay?: number;
}

const PatentCard = ({
  icon: Icon, 
  title, 
  description, 
  callouts, 
  alignment = 'left',
  delay = 0
}: PatentCardProps) => {
  const isCenter = alignment === 'center';

  const content = (
    <div className="h-full w-full bg-white border border-gray-300 rounded-lg shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-400 transition-[transform,box-shadow,border-color] duration-400 ease-out">
      <CardContent className="p-5 md:p-6 flex flex-col">
        <div className={`mb-3 flex justify-${isCenter ? 'center' : 'start'}`}>
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[hsl(var(--color-1))] to-[hsl(var(--color-3))] flex items-center justify-center">
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>
        <h3 className={`text-card-title flex items-start ${isCenter ? 'justify-center text-center' : ''} mb-2`}>
          {title}
        </h3>
        <p className={`text-body mb-3 md:mb-4 ${isCenter ? 'text-center' : 'text-left'}`}>
          {description}
        </p>
        <div className="space-y-1.5 md:space-y-2">
          {callouts.map((callout, index) => (
            <div key={index} className={`flex items-start justify-${isCenter ? 'center' : 'start'}`}>
              <CheckCircle className="h-5 w-5 bg-gradient-to-r from-[hsl(var(--color-1))] to-[hsl(var(--color-3))] text-white rounded-full flex-shrink-0 mr-2 mt-0.5" />
              <span className="text-neutral-700 text-sm md:text-base">{callout}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </div>
  );

  return alignment === 'center' ? content : <SimpleReveal delay={delay}>{content}</SimpleReveal>;
};

PatentCard.displayName = 'PatentCard';

export default React.memo(PatentCard);
